/**
 * One feature, at length: what it does, what it looks like, and why it matters in a document
 * people sign.
 *
 * REACHED FROM A TILE ON THE SPLASH, and readable before anybody signs in -- which is the whole
 * point of it. Somebody evaluating this product is deciding whether it does what their quality
 * system needs, and a sentence on a card cannot answer that.
 *
 * ITS OWN URL, so a link to it can be sent. The slug is stable; the tile's wording is not.
 */
import * as React from "react";
import { Box, Button, Typography } from "@mui/material";
import { ArrowBackOutlined, MenuBookOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Blue3, Grey2, Grey3, Grey5, Grey7, Grey17, White } from "../theme/styles";
import { nameBySlug, storyBySlug } from "./featureStories";

export default function FeatureStory() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const story = slug ? storyBySlug(slug) : undefined;
    const name = slug ? nameBySlug(slug) : undefined;

    // A SLUG NOBODY WROTE A PAGE FOR GOES BACK, rather than showing an empty frame with a title on
    // it. The splash only links tiles it has a story for, so arriving here otherwise means a stale
    // link -- and the catalogue is the useful thing to be given instead of an apology.
    React.useEffect(() => {
        if (!story) navigate("/", { replace: true });
    }, [story, navigate]);
    if (!story || !name) return null;

    return (
        <Box sx={{ minHeight: "100%", width: "100%", backgroundColor: White, pb: 12 }}>
            <Box sx={{ maxWidth: 820, mx: "auto", px: { xs: 4, md: 6 }, pt: { xs: 6, md: 10 } }}>

                <Button onClick={() => navigate("/")} startIcon={<ArrowBackOutlined/>}
                        sx={{ textTransform: "none", fontSize: 13, color: Grey7, pl: 0, mb: 5 }}>
                    Everything ParkourAI Docs does
                </Button>

                <Typography component="h1" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 600,
                                                 color: Grey17, lineHeight: 1.2 }}>
                    {name}
                </Typography>
                <Typography sx={{ fontSize: { xs: 16, md: 18 }, color: Grey7, mt: 3,
                                  lineHeight: 1.6 }}>
                    {story.hook}
                </Typography>

                {story.art && (
                    /* THE DRAWING SITS ON ITS OWN GROUND, not on the page's, so it reads as a
                       figure rather than as decoration that happens to be there. */
                    <Box sx={{ mt: 6, p: { xs: 3, md: 5 }, backgroundColor: Grey2,
                               border: `1px solid ${Grey3}`, borderRadius: 1 }}>
                        {story.art}
                    </Box>
                )}

                {story.what.map((paragraph, index) => (
                    <Typography key={index} sx={{ fontSize: 15, color: Grey7, mt: index ? 3 : 6,
                                                  lineHeight: 1.75 }}>
                        {paragraph}
                    </Typography>
                ))}

                {!!story.examples?.length && (
                    <Box sx={{ mt: 7 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em",
                                          color: Grey5, textTransform: "uppercase", mb: 3 }}>
                            In a procedure
                        </Typography>
                        {story.examples.map(example => (
                            <Box key={example.label} sx={{ mb: 4, pl: 3,
                                                           borderLeft: `2px solid ${Grey3}` }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600, color: Grey17 }}>
                                    {example.label}
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: Grey7, mt: 1,
                                                  lineHeight: 1.7 }}>
                                    {example.body}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* THE SENTENCE THAT MAKES IT A QUALITY FEATURE. Every one of these pages ends on
                    it, because the thing that distinguishes this product from a word processor is
                    never the feature -- it is what the feature is for in a document somebody signs. */}
                <Box sx={{ mt: 7, p: { xs: 3, md: 4 }, backgroundColor: "#E8F0FE",
                           borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em",
                                      color: Blue3, textTransform: "uppercase", mb: 1.5 }}>
                        Why it matters here
                    </Typography>
                    <Typography sx={{ fontSize: 15, color: Grey17, lineHeight: 1.7 }}>
                        {story.why}
                    </Typography>
                </Box>

                {story.guide && (
                    <Button startIcon={<MenuBookOutlined/>}
                            onClick={() => window.open(`/docs/${story.guide!.file}`, "_blank",
                                                       "noopener")}
                            sx={{ textTransform: "none", fontSize: 13, color: Grey7, mt: 5, pl: 0 }}>
                        {story.guide.label} — the full guide
                    </Button>
                )}
            </Box>
        </Box>
    );
}
